import * as XLSX from "xlsx";

function flattenColumns(cols) {
  return cols.flatMap((col) =>
    col.columns ? flattenColumns(col.columns) : col,
  );
}

function readExportCell(row, col) {
  if (
    col.accessorKey != null &&
    Object.prototype.hasOwnProperty.call(row, col.accessorKey)
  ) {
    return row[col.accessorKey];
  }
  if (col.id != null && Object.prototype.hasOwnProperty.call(row, col.id)) {
    return row[col.id];
  }
  if (typeof col.accessorFn === "function") {
    return col.accessorFn(row);
  }
  return "";
}

function sanitizeSheetName(name, fallback) {
  const cleaned = String(name || fallback || "Sheet")
    .replace(/[\\/?*[\]:]/g, " ")
    .trim();
  return (cleaned || fallback || "Sheet").slice(0, 31);
}

export function buildWorksheet(tableData, columns) {
  const sourceColumns = columns || [];
  const sourceRows = tableData || [];
  const flatColumns = flattenColumns(sourceColumns);
    const headers = flatColumns.map((col) => {
      if (typeof col.header === "function") {
        const headerVal = col.header();
        if (typeof headerVal === "string") return headerVal;
        if (headerVal && headerVal.props && headerVal.props.children)
          return headerVal.props.children;
        return "";
      }
      return col.header || "";
    });

    const hasGroups = sourceColumns.some((col) => col.columns);

    const groupHeaders = [];
    let merges = [];
    if (hasGroups) {
      let colIdx = 0;
      sourceColumns.forEach((col) => {
        if (col.columns) {
          const span = flattenColumns([col]).length;
          const label =
            typeof col.header === "function"
              ? col.header()?.props?.children || ""
              : col.header || "";
          groupHeaders.push(label);
          for (let i = 1; i < span; i++) groupHeaders.push("");

          merges.push({
            s: { r: 0, c: colIdx },
            e: { r: 0, c: colIdx + span - 1 },
          });
          colIdx += span;
        } else {
          const label =
            typeof col.header === "function"
              ? col.header()?.props?.children || ""
              : col.header || "";
          groupHeaders.push(label);

          colIdx += 1;
        }
      });
    }

    // Write by column index. Header text is not unique on the appraisal
    // screen: F1 and F2 both use "Result (Low value - good)". Keying rows by
    // that text drops the second score column.
    const exportRows = sourceRows.map((row) =>
      flatColumns.map((col) => {
        const value = readExportCell(row, col);
        return value == null ? "" : value;
      }),
    );

    const headerRows = hasGroups ? [groupHeaders, headers] : [headers];
    const ws = XLSX.utils.aoa_to_sheet(headerRows);
    if (hasGroups && merges.length > 0) {
      ws["!merges"] = merges;
    }
    if (exportRows.length > 0) {
      XLSX.utils.sheet_add_aoa(ws, exportRows, { origin: -1 });
    }
    return ws;
}

export function exportTablesToXLSX(sheets, fileName = "combined.xlsx") {
  const wb = XLSX.utils.book_new();
  const used = new Set();
  sheets.forEach((sheet, index) => {
    let name = sanitizeSheetName(sheet.name, `Sheet${index + 1}`);
    while (used.has(name)) {
      const suffix = ` ${used.size + 1}`;
      name = `${name.slice(0, 31 - suffix.length)}${suffix}`;
    }
    used.add(name);
    const ws = buildWorksheet(sheet.data || [], sheet.columns || []);
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  XLSX.writeFile(wb, fileName);
}

export function useExportTableToXLSX() {
  return (tableData, columns, fileName = "table.xlsx") => {
    exportTablesToXLSX(
      [{ name: "Sheet1", data: tableData, columns }],
      fileName,
    );
  };
}
